"use client";

import { useEffect, useState, useMemo } from "react";
import { createLogger } from "@/lib/logger";
import { useRouter } from "next/navigation";
import { useT } from "@/hooks/useT";

const logger = createLogger("UserProfileClient");
import UserProfile from "./UserProfile";
import { formatVolume } from "@/lib/polymarket";
import { detectAndStoreDextoolsUtm } from "@/lib/dextools-tracking";
import { useDextoolsSignupStore } from "@/stores/useDextoolsSignupStore";

type UsernameHistoryEntry = {
  old_username: string;
  new_username: string | null;
  changed_at: string;
};

type UserData = {
  username: string;
  wallet: string;
  avatar: string;
  bio: string;
  winRate: number;
  totalVolume: string;
  totalPnL?: number;
  profitPercent?: number;
  largestWin?: number;
  profileViews?: number;
  currentBalance?: number;
  positionsAboveEntry?: number;
  copiers: number;
  totalTrades: number;
  verified: boolean;
  joinedDate: string;
  markets: string[];
  recentTrades: any[];
  // Username history
  previousUsername?: string | null;
  usernameChangedAt?: string | null;
  usernameHistory?: UsernameHistoryEntry[];
  // X/Twitter
  xUsername?: string | null;
  // FrenFlow user enrichment
  isFrenFlowUser?: boolean;
  frenFlowUsername?: string | null;
  frenFlowName?: string | null;
  frenFlowAvatar?: string | null;
  // Predexon analytics
  tradingStyle?: string | null;
  secondaryStyles?: string[];
  avgTradeSize?: number;
  realizedPnl?: number | null;
  unrealizedPnl?: number | null;
};

type UserProfileClientProps = {
  identifier: string;
  initialUser?: any; // PolymarketUser from DB
  usernameHistory?: UsernameHistoryEntry[];
  children?: React.ReactNode; // RecentTradesSection from server
  source?: string;
};

export default function UserProfileClient({ identifier, initialUser, usernameHistory = [], children, source = 'polymarket' }: UserProfileClientProps) {
  const t = useT();
  const isKalshi = source === 'kalshi';
  const router = useRouter();

  // Transform initial user data immediately (before first render)
  const initialUserData = useMemo(() => {
    if (!initialUser) return null;

    // Parse string values to numbers
    const parseNumber = (value: any): number | undefined => {
      if (value === null || value === undefined) return undefined;
      const parsed = typeof value === 'string' ? parseFloat(value) : value;
      return isNaN(parsed) ? undefined : parsed;
    };

    // Parse market categories from stored string (no defaults - only show real data)
    let marketCategories: string[] = [];
    if (initialUser.market_categories) {
      try {
        const parsed = JSON.parse(initialUser.market_categories);
        if (Array.isArray(parsed) && parsed.length > 0) {
          marketCategories = parsed;
        }
      } catch {
        // Keep empty
      }
    }

    const base = {
      username: initialUser.username || `Trader${initialUser.address.slice(-4)}`,
      wallet: initialUser.address,
      avatar: (initialUser.profile_image && initialUser.profile_image.trim() !== "") ? initialUser.profile_image : null,
      bio: initialUser.bio || (isKalshi ? "Kalshi trader" : "Polymarket trader"),
      winRate: parseNumber(initialUser.win_rate) || 0,
      totalVolume: formatVolume(parseNumber(initialUser.volume) || 0),
      totalPnL: parseNumber(initialUser.pnl),
      profitPercent: parseNumber(initialUser.profit_percent),
      largestWin: parseNumber(initialUser.largestWin || initialUser.largest_win),
      profileViews: parseNumber(initialUser.profileViews || initialUser.profile_views),
      currentBalance: parseNumber(initialUser.positions_value),
      positionsAboveEntry: parseNumber(initialUser.gain),
      copiers: initialUser.copier_count || 0,
      totalTrades: parseNumber(initialUser.markets_traded) || 0,
      verified: false,
      joinedDate: initialUser.joinedDate || initialUser.polymarket_joined_at || initialUser.created_at || "Unknown",
      markets: marketCategories,
      recentTrades: [],
      // Username history
      previousUsername: initialUser.previous_username || null,
      usernameChangedAt: initialUser.username_changed_at || null,
      usernameHistory: usernameHistory,
      // X/Twitter
      xUsername: initialUser.xUsername || initialUser.x_username || null,
      // FrenFlow user enrichment
      isFrenFlowUser: initialUser.isFrenFlowUser || false,
      frenFlowUsername: initialUser.frenFlowUsername || null,
      frenFlowName: initialUser.frenFlowName || null,
      frenFlowAvatar: initialUser.frenFlowAvatar || null,
      // Predexon analytics
      tradingStyle: initialUser.tradingStyle || initialUser.trading_style || null,
      secondaryStyles: initialUser.secondaryStyles || initialUser.secondary_styles || [],
      avgTradeSize: parseNumber(initialUser.avgTradeSize || initialUser.avg_trade_size) || 0,
      realizedPnl: parseNumber(initialUser.realizedPnl || initialUser.realized_pnl),
      unrealizedPnl: parseNumber(initialUser.unrealizedPnl || initialUser.unrealized_pnl),
      // HFT flag from SSR. UserProfile seeds its useState from this so the
      // hero badge renders correctly on first paint — no style-badge flash.
      isHft: initialUser.isHft === true,
      tradesPerHour: typeof initialUser.tradesPerHour === 'number' ? initialUser.tradesPerHour : null,
    };

    // Kalshi-specific extra data
    if (isKalshi) {
      (base as any).followers = initialUser.followers || 0;
      (base as any).following = initialUser.following || 0;
      (base as any).badges = initialUser.badges || [];
      (base as any).categories = initialUser.categories || [];
      (base as any).rank = initialUser.rank || 0;
      (base as any).source = 'kalshi';
    }

    // Predict-specific extra data
    if (source === 'predict') {
      (base as any).totalPoints = initialUser.totalPoints || 0;
      (base as any).allocationRoundPoints = initialUser.allocationRoundPoints || 0;
      (base as any).positionsCount = initialUser.positionsCount || 0;
      (base as any).feeDiscountBps = initialUser.feeDiscountBps || 0;
      (base as any).badges = initialUser.badges || [];
      (base as any).categories = initialUser.categories || [];
      (base as any).weeklyPoints = initialUser.weeklyPoints || [];
      (base as any).topPositions = initialUser.topPositions || [];
      (base as any).pnlTimeseries = initialUser.pnlTimeseries || [];
      (base as any).rank = initialUser.rank || 0;
      (base as any).source = 'predict';
      base.bio = initialUser.description || initialUser.bio || 'Predict.fun trader';
      base.joinedDate = initialUser.joinedAt || initialUser.joined_at || base.joinedDate;
    }

    return base;
  }, [initialUser, usernameHistory, isKalshi]);

  const [user, setUser] = useState<UserData | null>(initialUserData);
  const [loading, setLoading] = useState(!initialUser);
  const [error, setError] = useState<string | null>(null);

  // ── DexTools copytrading interstitial context ─────────────────────────
  // When a visitor lands from the DexTools widget with utm_campaign=copytrading
  // (a deep-link into this trader profile), feed the global signup modal the
  // REAL trader context (handle, avatar, PnL, win rate, copiers, volume).
  // DextoolsWelcomeBanner reads this and renders the copy-trader mirror. Gated
  // on the `copytrading` campaign so predictions arrivals never trigger it here.
  const setTraderCtx = useDextoolsSignupStore((s) => s.setTraderCtx);
  useEffect(() => {
    try {
      const utm = detectAndStoreDextoolsUtm();
      if (!utm || utm.utmCampaign !== "copytrading" || !user) {
        setTraderCtx(null);
        return;
      }
      const raw = user.username || "";
      const isHex = /^0x[a-fA-F0-9]{10,}$/.test(raw);
      const traderName = raw && !isHex
        ? (isKalshi ? raw : `@${raw}`)
        : raw.startsWith("0x")
          ? `${raw.slice(0, 6)}…${raw.slice(-4)}`
          : (raw || identifier.replace("@", ""));
      // Raw numeric volume for the social-proof line (user.totalVolume is a
      // pre-formatted string). DexTools landings always have SSR initialUser.
      const volNum = Number(initialUser?.volume);
      setTraderCtx({
        traderName,
        traderAvatar: user.avatar || null,
        pnlUsd: typeof user.totalPnL === "number" ? user.totalPnL : null,
        winRate: typeof user.winRate === "number" ? user.winRate : null,
        copierCount: typeof user.copiers === "number" ? user.copiers : null,
        volumeUsd: Number.isFinite(volNum) && volNum > 0 ? volNum : null,
      });
    } catch {
      setTraderCtx(null);
    }
  }, [user, isKalshi, identifier, initialUser, setTraderCtx]);
  // Clear when leaving the profile so the modal doesn't linger elsewhere.
  useEffect(() => () => setTraderCtx(null), [setTraderCtx]);

  useEffect(() => {
    // If we already have initial data, skip fetch
    if (initialUser) {
      return;
    }

    // Fallback: fetch from API if no initial data
    async function fetchUserData() {
      try {
        const response = await fetch(`/api/traders/${encodeURIComponent(identifier)}`);

        if (!response.ok) {
          if (response.status === 404) {
            setError(t("traders.profile.notFound"));
            return;
          }
          throw new Error(t("traders.profile.fetchError"));
        }

        const data = await response.json();

        // Handle redirects
        if (data.redirectTo) {
          router.replace(data.redirectTo);
          return;
        }

        // API returns { trader: {...} } - transform to UserData format
        const trader = data.trader || data.user;
        if (trader) {
          const avatarUrl = (trader.avatar && trader.avatar.trim() !== "")
            ? trader.avatar
            : null;

          setUser({
            username: trader.username || `Trader${trader.address?.slice(-4)}`,
            wallet: trader.address,
            avatar: avatarUrl,
            bio: trader.bio || "Polymarket trader",
            winRate: trader.winRate || 0,
            totalVolume: typeof trader.totalVolume === 'number'
              ? formatVolume(trader.totalVolume)
              : trader.totalVolume || "$0",
            totalPnL: trader.totalPnL,
            profitPercent: trader.profitPercent,
            largestWin: trader.largestWin,
            profileViews: trader.profileViews,
            currentBalance: trader.currentBalance,
            positionsAboveEntry: trader.positionsAboveEntry,
            copiers: trader.copiers || 0,
            totalTrades: trader.tradeCount || trader.marketsCount || 0,
            verified: false,
            joinedDate: trader.joinedDate || "Unknown",
            markets: trader.marketCategories ?
              (typeof trader.marketCategories === 'string'
                ? JSON.parse(trader.marketCategories)
                : trader.marketCategories)
              : [],
            recentTrades: [],
            // FrenFlow user enrichment
            isFrenFlowUser: trader.isFrenFlowUser || false,
            frenFlowUsername: trader.frenFlowUsername || null,
            frenFlowName: trader.frenFlowName || null,
            frenFlowAvatar: trader.frenFlowAvatar || null,
            // Predexon analytics
            tradingStyle: trader.tradingStyle || null,
            secondaryStyles: trader.secondaryStyles || [],
            avgTradeSize: trader.avgTradeSize || 0,
            realizedPnl: trader.realizedPnl,
            unrealizedPnl: trader.unrealizedPnl,
          });
        }
      } catch (err) {
        logger.error("Error fetching trader", { error: err instanceof Error ? err.message : String(err) });
        setError(t("traders.profile.loadError"));
      } finally {
        setLoading(false);
      }
    }

    fetchUserData();
  }, [identifier, initialUser, router]);

  // Enrich profile with Predexon wallet analytics (trading style, PnL, metrics)
  // Works for Polymarket (EVM) and Predict.fun (EVM/BSC) — Kalshi uses nicknames, not wallets
  const [predexonProfile, setPredexonProfile] = useState<any>(null);
  const hasValidWallet = !!user?.wallet && !isKalshi && /^0x[a-f0-9]{40}$/.test(user.wallet.toLowerCase());
  // Starts true for wallets we actually expect data for, so the mobile stats panel
  // can reserve space via a skeleton and avoid the big CLS when data arrives.
  const [predexonLoading, setPredexonLoading] = useState<boolean>(hasValidWallet);
  useEffect(() => {
    if (!user?.wallet || isKalshi) {
      setPredexonLoading(false);
      return;
    }
    const addr = user.wallet.toLowerCase();
    if (!/^0x[a-f0-9]{40}$/.test(addr)) {
      setPredexonLoading(false);
      return;
    }

    fetch(`/api/analytics/wallet/${addr}`)
      .then(r => r.ok ? r.json() : null)
      .then(result => {
        if (!result?.success || !result.data) return;
        // Anti-stale guard: `stale` means the live Predexon (Dev) call failed
        // and the backend served the last-good cache snapshot, which can be
        // arbitrarily old during a full outage. Skip it entirely so the stats
        // panel stays hidden and the hero keeps its fresh DB/PM values instead
        // of showing stale win-rate / ROI / style as if they were current.
        if (result.stale) return;
        const d = result.data;
        setPredexonProfile(d);
        setUser(prev => prev ? {
          ...prev,
          tradingStyle: d.primary_style || d.trading_styles?.primary_style || prev.tradingStyle,
          secondaryStyles: d.secondary_styles || prev.secondaryStyles,
          avgTradeSize: d.avg_trade_size || prev.avgTradeSize,
          winRate: d.win_rate > 0 ? Math.round(d.win_rate * 100) : prev.winRate,
          totalTrades: d.total_trades || prev.totalTrades,
          realizedPnl: d.realized_pnl ?? prev.realizedPnl,
          unrealizedPnl: d.unrealized_pnl ?? prev.unrealizedPnl,
          // ROI from Predexon all-time metrics (decimal → percent).
          // Lifts it onto `user` so the fallback Performance card and the
          // mini-stats strip can render it even if the full PredexonStatsPanel
          // isn't mounted on this layout breakpoint.
          profitPercent: typeof d?.metrics?.all_time?.roi === 'number'
            ? d.metrics.all_time.roi * 100
            : prev.profitPercent,
        } : prev);
      })
      .catch(() => {}) // Silent fallback
      .finally(() => setPredexonLoading(false));
  }, [user?.wallet, isKalshi]);

  if (loading) {
    return <UserProfileSkeleton />;
  }

  if (error || !user) {
    return (
      <div className="min-h-[85vh] flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 w-20 h-20 rounded-[5px] bg-gray-100 dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] flex items-center justify-center">
            <svg className="w-10 h-10 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {error || t("traders.profile.errorTitle")}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
            {t("traders.profile.errorDesc")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="/traders"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white dark:text-black bg-gray-900 dark:bg-white rounded-[5px] hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              {t("traders.profile.browse")}
            </a>
            <a
              href="/"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              {t("traders.profile.goHome")}
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <UserProfile user={user} source={source} predexonProfile={predexonProfile} predexonLoading={predexonLoading}>
      {children}
    </UserProfile>
  );
}

function UserProfileSkeleton() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-violet-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 pt-24">
      {/* Header Skeleton */}
      <div className="px-4 sm:px-6 lg:px-12 pb-8">
        <div className="animate-pulse">
          {/* Back button skeleton */}
          <div className="h-6 w-20 bg-gray-200 dark:bg-gray-800 rounded mb-6" />

          {/* Profile Header */}
          <div className="bg-white dark:bg-gray-900 rounded-[5px] border border-gray-100 dark:border-gray-800 p-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Avatar skeleton */}
              <div className="w-24 h-24 bg-gray-200 dark:bg-gray-800 rounded-[5px]" />

              <div className="flex-1 space-y-4 w-full">
                {/* Username skeleton */}
                <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-48" />

                {/* Wallet skeleton */}
                <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded w-64" />

                {/* Stats skeleton */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="space-y-2">
                      <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-20" />
                      <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded w-24" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Trades Skeleton */}
      <section className="px-4 sm:px-6 lg:px-12 pb-12">
        <div className="bg-white dark:bg-gray-900 rounded-[5px] border border-gray-100 dark:border-gray-800 p-8">
          <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-48 mb-6" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 p-4 border border-gray-200 dark:border-gray-700 rounded-[5px] animate-pulse">
                <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-[5px]" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                </div>
                <div className="text-right">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16 mb-2" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-12" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
